import React, { ComponentType, useContext } from 'react'
import { Button } from '@/components/ui/button'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridItemChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { ClientNoteDto } from 'api/Clients/Types/ClientNoteDto'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Loading } from 'components/Loading'
import { NotesCommandCell } from 'components/Note/NotesCommandCell'
import { useNoteState } from 'components/Note/noteState'
import { NoteValue } from 'components/Note/types'
import { errorToast, ToastContext } from 'components/Toast/Toast'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { dateComparator } from 'utils/date'
import { hasPermission } from 'utils/permissionsValidator'

import { clientNotesColumn } from '../../clientNotesColumns'

interface ClientDetailsBackOfficeNotesProps {
  notes: ClientNoteDto[]
  permissions: PermissionDto[]
  onItemChange(event: GridItemChangeEvent): void
  onAddNew(): void
  actionCell: ComponentType<GridCellProps>
}

const ClientDetailsBackOfficeNotesUI: React.FC<ClientDetailsBackOfficeNotesProps> = (props) => {
  const { notes, permissions, actionCell, onItemChange, onAddNew } = props

  return (
    <Grid
      resizable
      data={[...notes].sort((a, b) => dateComparator(a.createdDate, b.createdDate, true))}
      onItemChange={onItemChange}
      editField='inEdit'
    >
      <GridToolbar>
        <Button
          onClick={onAddNew}
          size='xs'
          disabled={!hasPermission(permissions, PermissionsEnum.CanAddClientNotes)}
        >
          Add new
        </Button>
      </GridToolbar>
      {clientNotesColumn
        .filter((c) => c.show !== false)
        .map((column, idx) => (
          <Column key={idx + 2} {...column} />
        ))}
      <Column cell={actionCell} width='150px' />
    </Grid>
  )
}

interface OuterProps {
  data: ClientDetailedDto
}

export const ClientDetailsBackOfficeNotes: React.FC<OuterProps> = (props) => {
  const { data } = props

  const clientsApiClient = useApiClient(ClientsApiClient)
  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(() => {
    return clientsApiClient.getClientNotes(data.id)
  }, [])
  const { data: notesData = [], isLoading } = useFetchOne(fetchData)

  const permissions = auth?.permissions || []

  const handleAdd = async (dataItem: ClientNoteDto) => {
    if (data?.id) {
      await clientsApiClient.createClientNote({
        clientId: data.id,
        description: dataItem.description,
      })
    }
  }

  const handleUpdate = async (dataItem: ClientNoteDto) => {
    if (data?.id) {
      await clientsApiClient.updateClientNote({
        id: dataItem.id as string,
        description: dataItem.description,
      })
    }
  }

  const handleRemove = async (dataItem: ClientNoteDto) => {
    if (dataItem?.id) {
      try {
        await clientsApiClient.removeClientNote(dataItem.id)
      } catch (error) {
        const err = error as { message: string }
        if (dataItem.createdBy?.id !== auth?.user.id) {
          setToast(errorToast('Cannot delete note. Note not created by user.'))
        } else {
          setToast(errorToast(err.message))
        }
        throw error
      }
    }
  }

  const handleAddNew = () => {
    addNew({
      id: null,
      description: null,
      client: { id: data.id, name: data.firstname },
      Discontinued: false,
    })
  }

  const handleEdit = (note: NoteValue<ClientNoteDto>) => {
    setEditNote(note)
  }

  const { notes, setNotes, setEditNote, addNew, ...noteState } = useNoteState({
    onAdd: handleAdd,
    onEdit: handleEdit,
    onRemove: handleRemove,
    onUpdate: handleUpdate,
    forceRefresh,
    notes: notesData,
  })

  return (
    <Loading isLoading={isLoading}>
      <ClientDetailsBackOfficeNotesUI
        notes={notes}
        permissions={permissions}
        onAddNew={handleAddNew}
        onItemChange={noteState.itemChange}
        actionCell={NotesCommandCell({
          ...noteState,
          CanEdit: hasPermission(permissions, PermissionsEnum.CanEditBackofficeNotes),
          CanDelete: hasPermission(permissions, PermissionsEnum.CanDeleteBackofficeNotes),
        })}
      />
    </Loading>
  )
}
